import { useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { useMutation } from 'react-query';
import type { Terminal } from '@xterm/xterm';
import { getSocketUrl, sshApi } from '../services/api';
import { Button, IconButton, Input, Modal, MonoCell } from '@/components/ds-zta';
import TerminalSurface, { type TerminalSize } from '@/components/terminal/TerminalSurface';
import TerminalChrome from '@/components/terminal/TerminalChrome';
import { connectionStatus } from '@/components/terminal/connectionStatus';


interface XTermSSHTerminalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnectionKill?: () => void;
  resource: {
    id: string;
    external_id: string;
    name?: string;
    endpoint?: {
      host?: string;
      port?: number;
    };
  };
  defaultUser?: string;
  /**
   * Ignored. This dialog used to record its own canvas with MediaRecorder and
   * upload it as the session's footage. The server's record of the session is
   * the evidence (design eval F20, D2 = A), and the only caller that turned
   * this on was SSHResources' in-page terminal, which was never opened and is
   * gone (F40). Kept so existing callers still type-check.
   */
  enableRecording?: boolean;
}


export default function XTermSSHTerminal({
  isOpen,
  onClose,
  onConnectionKill,
  resource,
  defaultUser = 'root',
}: XTermSSHTerminalProps) {
  const [user, setUser] = useState(defaultUser);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showConnectionForm, setShowConnectionForm] = useState(true);

  const wsRef = useRef<WebSocket | null>(null);
  const xtermRef = useRef<Terminal | null>(null);
  const sessionCreationAttemptsRef = useRef<number>(0);
  const sessionIdRef = useRef<string | null>(null);

  const maxSessionAttempts = 5;

  // Close the WebSocket so the backend read-loop exits and finalizes the session
  // recording immediately, instead of relying on the 30s server keepalive to
  // notice the browser is gone. Strip handlers first so onerror/onclose can't
  // kick off a reconnect on a socket we're deliberately tearing down.
  const closeSocketQuietly = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try { ws.close(); } catch { /* already closing */ }
    wsRef.current = null;
  }, []);

  // Closing the dialog tears the connection down.
  useEffect(() => {
    if (!isOpen) return;
    return closeSocketQuietly;
  }, [isOpen, closeSocketQuietly]);

  // Close the terminal socket when the tab/window goes away so the backend stops the
  // recording promptly (beforeunload doesn't fire on mobile/bfcache — pagehide does).
  useEffect(() => {
    window.addEventListener('beforeunload', closeSocketQuietly);
    window.addEventListener('pagehide', closeSocketQuietly);
    return () => {
      window.removeEventListener('beforeunload', closeSocketQuietly);
      window.removeEventListener('pagehide', closeSocketQuietly);
    };
  }, [closeSocketQuietly]);

  // Handle input - send each keystroke directly to the SSH server
  // The server handles echoing and cursor positioning via PTY
  const handleData = useCallback((data: string) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(new TextEncoder().encode(data));
      } catch (error) {
        console.error('Error sending input:', error);
      }
    }
  }, []);

  // Send resize message to sync PTY dimensions
  const handleResize = useCallback(({ cols, rows }: TerminalSize) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'resize', cols, rows }));
    }
  }, []);


  const connectToWebSocket = useCallback((sessionId: string, attempt: number = 1) => {
    // The hub's client socket carries no token in its URL; the session id is
    // the capability. Encoded, like every value put in a socket URL (F14).
    const wsUrl = `${getSocketUrl()}/agent/client/${encodeURIComponent(sessionId)}/ws`;

    if (xtermRef.current) {
      xtermRef.current.write(`\r\nConnecting to SSH (attempt ${attempt})...\r\n`);
    }


    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer'; // Ensure binary data handling
    wsRef.current = ws;


    ws.onopen = () => {
      if (xtermRef.current) {
        xtermRef.current.write('\r\nConnected\r\n\r\n');
      }
      setIsConnected(true);
      setIsConnecting(false);
      setShowConnectionForm(false);
      sessionCreationAttemptsRef.current = 0;

      // Send initial terminal size to sync PTY dimensions (delay for SSH connection to establish)
      setTimeout(() => {
        if (xtermRef.current && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'resize',
            cols: xtermRef.current.cols,
            rows: xtermRef.current.rows,
          }));
        }
      }, 1000);
    };


    ws.onmessage = async (event) => {
      try {
        let data: string | ArrayBuffer;

        if (event.data instanceof Blob) {
          data = await event.data.arrayBuffer();
        } else if (event.data instanceof ArrayBuffer) {
          data = event.data;
        } else {
          data = event.data;
        }


        let text: string;
        if (data instanceof ArrayBuffer) {
          text = new TextDecoder().decode(data);
        } else {
          text = data.toString();
        }


        if (xtermRef.current && text) {
          xtermRef.current.write(text);

          // Detect "Agent offline" message and trigger reconnection
          if (text.toLowerCase().includes('agent offline')) {
            if (xtermRef.current) {
              xtermRef.current.write('\r\n\r\nAgent offline detected. Attempting to reconnect...\r\n');
            }

            // Close current connection
            if (wsRef.current) {
              wsRef.current.close();
            }

            // Retry connection after delay
            setTimeout(() => {
              if (sessionId && sessionCreationAttemptsRef.current < maxSessionAttempts) {
                sessionCreationAttemptsRef.current++;
                connectToWebSocket(sessionId, sessionCreationAttemptsRef.current + 1);
              } else {
                // Try creating a new session
                if (resource?.external_id && user) {
                  if (xtermRef.current) {
                    xtermRef.current.write('Attempting to create new session...\r\n');
                  }
                  sessionCreationAttemptsRef.current = 0;
                  createSessionMutation.mutate({
                    serverID: resource.external_id,
                    resourceID: resource.id,
                    user: user.trim()
                  });
                }
              }
            }, 3000);
          }
        }
      } catch (err) {
        console.error('Error processing SSH message:', err);
      }
    };


    ws.onclose = () => {
      if (xtermRef.current && isConnected) {
        xtermRef.current.write('\r\n\r\nConnection closed\r\n');
      }
      setIsConnected(false);
      setIsConnecting(false);
      wsRef.current = null;
    };


    ws.onerror = () => {
      setIsConnecting(false);

      if (sessionCreationAttemptsRef.current < maxSessionAttempts) {
        sessionCreationAttemptsRef.current++;
        setTimeout(() => {
          if (sessionId) {
            connectToWebSocket(sessionId, sessionCreationAttemptsRef.current + 1);
          }
        }, 2000);
      } else {
        if (xtermRef.current) {
          xtermRef.current.write('\r\nConnection failed\r\n');
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, maxSessionAttempts, resource?.external_id, user]);


  // Create SSH session mutation with retry
  const createSessionMutation = useMutation({
    mutationFn: ({ serverID, resourceID, user }: { serverID: string; resourceID: string; user: string }) =>
      sshApi.createSession(serverID, resourceID, user, xtermRef.current?.cols, xtermRef.current?.rows),
    onSuccess: (data) => {
      // The response is not logged: it names the session (design eval F14).
      const newSessionId = data?.data?.session_id;

      sessionIdRef.current = newSessionId;
      sessionCreationAttemptsRef.current = 0;
      connectToWebSocket(newSessionId, 1);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error';
      if (xtermRef.current) {
        xtermRef.current.write(`\r\nSession error: ${errorMessage}\r\n`);
      }

      if (sessionCreationAttemptsRef.current < maxSessionAttempts) {
        sessionCreationAttemptsRef.current++;
        if (xtermRef.current) {
          xtermRef.current.write(`Retrying in 2 seconds...\r\n`);
        }
        setTimeout(() => {
          if (resource?.external_id && user) {
            createSessionMutation.mutate({ serverID: resource.external_id, resourceID: resource.id, user: user.trim() });
          }
        }, 2000);
      } else {
        setIsConnecting(false);
        if (xtermRef.current) {
          xtermRef.current.write('\r\nFailed after multiple attempts\r\n');
        }
      }
    },
  });


  const handleConnect = () => {
    if (!resource?.external_id || !user?.trim()) return;


    setIsConnecting(true);
    sessionCreationAttemptsRef.current = 0;

    if (xtermRef.current) {
      xtermRef.current.clear();
      xtermRef.current.write(`Connecting to ${resource.name || resource.external_id} as ${user}...\r\n`);
    }

    createSessionMutation.mutate({
      serverID: resource.external_id,
      resourceID: resource.id,
      user: user.trim()
    });
  };


  const handleKillConnection = () => {
    // Notify parent about connection kill
    onConnectionKill?.();

    if (wsRef.current) {
      wsRef.current.close();
    }

    if (xtermRef.current) {
      xtermRef.current.clear();
      xtermRef.current.write('Connection terminated. Ready for new connection.\r\n\r\n');
    }

    if (sessionIdRef.current) sshApi.terminateSession(sessionIdRef.current);

    sessionIdRef.current = null;
    setIsConnected(false);
    setIsConnecting(false);
    setShowConnectionForm(true);
    sessionCreationAttemptsRef.current = 0;
    wsRef.current = null;
  };


  const handleClose = () => {
    // Notify parent about connection kill
    onConnectionKill?.();

    if (wsRef.current) {
      try {
        wsRef.current.send(new TextEncoder().encode('\x04'));
        setTimeout(() => wsRef.current?.close(), 500);
      } catch (error) {
        wsRef.current.close();
      }
    }

    if (xtermRef.current) {
      xtermRef.current.clear();
    }

    if (sessionIdRef.current) sshApi.terminateSession(sessionIdRef.current);

    sessionIdRef.current = null;
    setIsConnected(false);
    setIsConnecting(false);
    setShowConnectionForm(true);
    wsRef.current = null;

    onClose();
  };


  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="xl"
      showCloseButton={false}
      title="SSH Terminal"
      description={`— ${resource.name || resource.external_id}`}
      className="h-[92vh] w-[80rem] [&_.rc-modal__body]:flex [&_.rc-modal__body]:flex-col [&_.rc-modal__body]:overflow-hidden [&_.rc-modal__body]:p-0"
    >
      <TerminalChrome
        className="flex-1"
        status={connectionStatus(isConnected, isConnecting)}
        actions={
          <>
            {isConnected && (
              <Button
                variant="danger"
                size="sm"
                onClick={handleKillConnection}
                title="Kill connection and start new"
              >
                Kill & Restart
              </Button>
            )}
            <IconButton
              aria-label="Close terminal"
              size="sm"
              icon={<X />}
              onClick={handleClose}
            />
          </>
        }
        toolbar={showConnectionForm && !isConnected && (
          <div className="flex shrink-0 items-end gap-3 border-b border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)] px-5 py-3">
            <Input
              label="Username"
              type="text"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleConnect()}
              disabled={isConnecting}
              className="w-44"
              placeholder="root"
            />
            <Button onClick={handleConnect} disabled={isConnecting || !user.trim()}>
              {isConnecting ? 'Connecting…' : 'Connect'}
            </Button>
          </div>
        )}
        // The connection state is the header's status chip, once (design
        // eval F47: a footer "Ready" beside a header "Disconnected").
        footer={
          <div className="flex items-center gap-4">
            {resource.external_id && <MonoCell value={resource.external_id} />}
            {sessionIdRef.current && (
              <span className="flex min-w-0 items-center gap-1">
                session: <MonoCell value={sessionIdRef.current} width="6rem" />
              </span>
            )}
          </div>
        }
      >
        <TerminalSurface
          className="w-full flex-1 px-2 py-1"
          autoFocus
          aria-label={`SSH terminal for ${resource.name || resource.external_id}`}
          onReady={(term) => { xtermRef.current = term; }}
          onDispose={() => { xtermRef.current = null; }}
          onData={handleData}
          onResize={handleResize}
        />
      </TerminalChrome>
    </Modal>
  );
}
